import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, ArrowLeft, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import api from "../lib/api";

const SBL_LOGO = "/sbl-logo.jpeg";

const VERIFIED_GRADUATES = [
  "AUGUSTINA AMEH AROME",
  "ANDREW OYEDELE",
  "OLAOLUWA OWOLU",
  "SYLVIA ENI",
  "NWOKOCHA HAPPINESS",
  "OLUWAYEMISI RUTH LAWAL",
  "JOY YEIYAH",
  "CHILIPO GUNDA",
  "DEBORAH FOLASHADE AGUNBIADE",
  "ABOSEDE GABRIEL",
  "RACHAEL ESANOLA",
  "OLUWAFUNMILAYO ADELOKUN",
  "OLUWATUNMISE LASAKI",
  "EMILY LOKO",
  "AKOTEYON JOSHUA",
  "OLUJIMI OLUFEMI",
  "PAUL ADETONA",
];

export default function VerifyCertificatePage() {
  const { certNumber } = useParams();
  const [directStatus, setDirectStatus] = useState("loading");
  const [directCert, setDirectCert] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    if (!certNumber) {
      setDirectStatus("generic");
      return;
    }
    const verify = async () => {
      try {
        const { data } = await api.get(`/certificates/verify/${certNumber}`);
        setDirectCert(data);
        setDirectStatus("verified");
      } catch {
        setDirectStatus("not_found");
      }
    };
    verify();
  }, [certNumber]);

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchResults(null);

    // Round 38 — Task 11: unified lookup accepts admission number OR name.
    try {
      const { data } = await api.get(`/certificates/verify-lookup?query=${encodeURIComponent(q)}`);
      if (Array.isArray(data) && data.length > 0) {
        // Single hit → show as cert; multiple → show as list.
        if (data.length === 1) {
          setSearchResults({ type: "cert", data: data[0] });
        } else {
          setSearchResults({ type: "db_name", data });
        }
        setSearching(false);
        return;
      }
    } catch {}

    // Try direct certificate-number lookup as a secondary path.
    try {
      const { data } = await api.get(`/certificates/verify/${encodeURIComponent(q)}`);
      if (data && data.verified) {
        setSearchResults({ type: "cert", data });
        setSearching(false);
        return;
      }
    } catch {}

    // Fallback: legacy verified graduates list.
    const upper = q.toUpperCase();
    const found = VERIFIED_GRADUATES.some(name =>
      name === upper || name.includes(upper) || upper.includes(name) ||
      name.split(" ").every(part => upper.includes(part))
    );
    if (found) {
      setSearchResults({ type: "legacy", name: q });
      setSearching(false);
      return;
    }

    setSearchResults({ type: "not_found" });
    setSearching(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="SBL" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              School Builder Lab Africa
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full space-y-6">

          {/* Direct URL verification states */}
          {certNumber && directStatus === "loading" && (
            <div className="text-center py-12" data-testid="verify-loading">
              <div className="animate-pulse text-slate-400 text-sm tracking-widest uppercase">Verifying certificate...</div>
            </div>
          )}

          {certNumber && directStatus === "verified" && directCert && (
            <div className="bg-white border border-slate-200 shadow-sm p-8 sm:p-10 text-center" data-testid="verify-success">
              <img src={SBL_LOGO} alt="SBL" className="w-14 h-14 object-contain rounded-sm mx-auto mb-5" />
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={40} className="text-emerald-600" weight="fill" />
              </div>
              <h1 className="text-xl font-bold text-emerald-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Certificate Verified</h1>
              <p className="text-sm text-slate-500 mb-5">This is an authentic certificate issued by School Builder Lab Africa.</p>
              <div className="bg-slate-50 border border-slate-200 p-4 text-left space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Certificate No.</span><span className="font-semibold text-slate-900 font-mono">{directCert.cert_number}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Student Name</span><span className="font-semibold text-slate-900">{directCert.student_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Programme / Course</span><span className="font-semibold text-slate-900">{directCert.title}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Type</span><span className="font-semibold text-slate-900 capitalize">{directCert.type}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Date Issued</span><span className="font-semibold text-slate-900">{directCert.issued_date}</span></div>
              </div>
            </div>
          )}

          {certNumber && directStatus === "not_found" && (
            <div className="bg-white border border-slate-200 shadow-sm p-8 sm:p-10 text-center" data-testid="verify-failed">
              <img src={SBL_LOGO} alt="SBL" className="w-14 h-14 object-contain rounded-sm mx-auto mb-5" />
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle size={40} className="text-red-500" weight="fill" />
              </div>
              <h1 className="text-xl font-bold text-red-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Certificate Not Found</h1>
              <p className="text-sm text-slate-600 mb-4">No certificate matching <strong className="font-mono">{certNumber}</strong> was found in our records.</p>
              <p className="text-xs text-slate-500">Contact us at <a href="mailto:support@schoolbuilderlab.net.ng" className="text-blue-800 hover:underline">support@schoolbuilderlab.net.ng</a></p>
            </div>
          )}

          {/* Generic search portal (no certNumber in URL) */}
          {!certNumber && (
            <>
              <div className="bg-white border border-slate-200 shadow-sm p-8 sm:p-10 text-center" data-testid="verify-generic">
                <img src={SBL_LOGO} alt="SBL" className="w-14 h-14 object-contain rounded-sm mx-auto mb-5" />
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle size={32} className="text-emerald-600" weight="fill" />
                </div>
                <h1 className="text-xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Certificate Verification Portal
                </h1>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Verify the authenticity of certificates and graduates of <strong>School Builder Lab Africa</strong>. Search by certificate number or graduate name.
                </p>

                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Certificate number, admission number, or graduate name"
                      value={query}
                      onChange={e => { setQuery(e.target.value); setSearchResults(null); }}
                      onKeyDown={e => e.key === "Enter" && handleSearch()}
                      className="pl-9 rounded-none"
                      data-testid="verify-search-input"
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={searching}
                    className="bg-blue-800 text-white hover:bg-blue-900 rounded-none px-6"
                    data-testid="verify-search-btn"
                  >
                    {searching ? "Searching..." : "Verify"}
                  </Button>
                </div>

                <div className="bg-slate-50 border border-slate-200 p-3 mt-5 text-left text-sm text-slate-600">
                  <p className="font-semibold text-slate-800 mb-1 text-xs">Direct verification link:</p>
                  <code className="block text-xs bg-slate-100 p-2 text-blue-800 break-all">
                    schoolbuilderlab.net.ng/verify/SBL-XXXX-XXXXXXXX
                  </code>
                </div>
              </div>

              {/* Search Results */}
              {searchResults && searchResults.type === "cert" && (
                <div className="bg-white border border-emerald-200 shadow-sm p-6 sm:p-8" data-testid="search-result-cert">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle size={28} className="text-emerald-600 flex-shrink-0" weight="fill" />
                    <div>
                      <p className="text-base font-bold text-emerald-800">Certificate Verified</p>
                      <p className="text-xs text-emerald-600">This is an authentic certificate issued by School Builder Lab Africa.</p>
                    </div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 p-4 text-left space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Certificate No.</span><span className="font-semibold text-slate-900 font-mono">{searchResults.data.cert_number}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Student Name</span><span className="font-semibold text-slate-900">{searchResults.data.student_name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Programme / Course</span><span className="font-semibold text-slate-900">{searchResults.data.title}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Type</span><span className="font-semibold text-slate-900 capitalize">{searchResults.data.type}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Date Issued</span><span className="font-semibold text-slate-900">{searchResults.data.issued_date}</span></div>
                  </div>
                </div>
              )}

              {searchResults && searchResults.type === "db_name" && (
                <div className="bg-white border border-emerald-200 shadow-sm p-6 sm:p-8 space-y-4" data-testid="search-result-db">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle size={28} className="text-emerald-600 flex-shrink-0" weight="fill" />
                    <div>
                      <p className="text-base font-bold text-emerald-800">Graduate Verified</p>
                      <p className="text-xs text-emerald-600">{searchResults.data.length} certificate(s) found in our records.</p>
                    </div>
                  </div>
                  {searchResults.data.map((cert, i) => (
                    <div key={i} className="bg-slate-50 border border-slate-200 p-4 text-left space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-500">Certificate No.</span><span className="font-semibold text-slate-900 font-mono">{cert.cert_number}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Student Name</span><span className="font-semibold text-slate-900">{cert.student_name}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Programme / Course</span><span className="font-semibold text-slate-900">{cert.title}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Type</span><span className="font-semibold text-slate-900 capitalize">{cert.type}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Date Issued</span><span className="font-semibold text-slate-900">{cert.issued_date}</span></div>
                    </div>
                  ))}
                </div>
              )}

              {searchResults && searchResults.type === "legacy" && (
                <div className="bg-white border border-emerald-200 shadow-sm p-6 sm:p-8" data-testid="search-result-legacy">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={28} className="text-emerald-600 flex-shrink-0" weight="fill" />
                    <div>
                      <p className="text-base font-bold text-emerald-800">Graduate Verified</p>
                      <p className="text-xs text-emerald-700"><strong>{searchResults.name}</strong> is a confirmed graduate of School Builder Lab Africa (Cohort One).</p>
                    </div>
                  </div>
                </div>
              )}

              {searchResults && searchResults.type === "not_found" && (
                <div className="bg-white border border-red-200 shadow-sm p-6 sm:p-8" data-testid="search-result-not-found">
                  <div className="flex items-center gap-3">
                    <XCircle size={28} className="text-red-500 flex-shrink-0" weight="fill" />
                    <div>
                      <p className="text-base font-bold text-red-800">Not Found</p>
                      <p className="text-xs text-red-600">No certificate or graduate record found for "<strong>{query}</strong>". Please check the spelling and try again.</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <footer className="bg-slate-950 text-slate-400 py-6 text-center text-xs">
        <p>&copy; {new Date().getFullYear()} School Builder Lab Africa. All rights reserved.</p>
      </footer>
    </div>
  );
}
